/* eslint-disable indent */
import React from 'react';
import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { Avatar } from '@/components/Avatar';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { StatusTag } from '@/components/Tag/StatusTag';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { useUserRole, useAuth, useLoginByUserId } from '@/hooks/auth.hooks';
import { AuthResponse } from '@/api/entities/auth';
import { clearLocalCache, clearStorage } from '@/utils/storage.utils';
import { setAccessToken } from '@/utils/token.utils';
import { EditUserTab } from '@/type/ui/tab/userTab';

export const UserTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const { hasNoRole, isAdmin, isModerator } = useUserRole();
  const { setLoggedInUser, loggedInUser } = useAuth();

  const colSpan = hasNoRole ? 6 : 7;

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  const { mutate: loginByUserId } = useLoginByUserId({
    handleSuccess: (data: AuthResponse) => {
      // Clear old admin session state
      clearStorage();
      clearLocalCache();
      // Set new user session
      setAccessToken(data?.data?.access_token);
      setLoggedInUser(data?.data?.user);
      // Reload page to ensure AuthProvider reinitializes with new token and fetches correct roles
      // window.location.href = '/';
      navigate('/');
    },
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={colSpan}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/user/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        const numberOfVehiculeChecks = entry?.vehiculeChecks?.length || 0;

        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            {!hasNoRole && !isModerator && (
              <TableCell
                tdClassName="sticky-select-column"
                content={
                  <Checkbox
                    checked={Boolean(
                      selectedTableData?.find((item) => item?.id === entry?.id),
                    )}
                    onChange={() => onSelectedItem(entry?.id)}
                  />
                }
              />
            )}
            <TableCell
              content={
                <div className="flex items-center gap-1.5">
                  <Avatar
                    imageUrl={entry?.profilePicture}
                    containerClassName="w-[30px] h-[30px] rounded-full border-0 shadow-0"
                  />
                  <div>
                    {entry?.firstname} {entry?.lastname}
                  </div>
                </div>
              }
            />
            <TableCell
              tdClassName="text-center"
              content={
                <StatusTag
                  text={`${
                    entry?.active ? t('common:active') : t('common:inactive')
                  }`}
                  className={twMerge(
                    'pt-[6px] pb-[5px] text-[10.1px] font-semibold [word-spacing:1.9px] leading-snug uppercase',
                    entry?.active &&
                      'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                    !entry?.active &&
                      'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
                  )}
                />
              }
            />
            <TableCell
              content={
                <div className="flex flex-col gap-2">
                  {entry?.entityRoles?.filter((item) => item?.groupId)
                    ?.length === 0
                    ? '--'
                    : entry?.entityRoles?.map((entityRole, index) => {
                        if (entityRole?.group) {
                          return (
                            <div
                              className="flex gap-2"
                              key={`entity-role-${index}`}
                            >
                              <div className="w-4 h-4 min-w-4 flex items-center justify-center relative text-2xs text-brand ring-1 ring-brand-clarity bg-brand-light rounded-full">
                                {entityRole?.group?.name
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </div>
                              <span className="text-xs">
                                {entityRole?.group?.name}
                              </span>
                            </div>
                          );
                        }

                        return <React.Fragment key={`entity-role-${index}`} />;
                      })}
                </div>
              }
            />
            <TableCell
              content={<div className="break-all">{entry?.email}</div>}
            />
            <TableCell
              content={
                <CellContentWithRedirect
                  text={numberOfVehiculeChecks}
                  disabled={!numberOfVehiculeChecks}
                  onClick={() =>
                    navigate(
                      `/user/edit/${entry?.id}?tab=${EditUserTab.VEHICULE_CHECKS}`,
                    )
                  }
                />
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/user/edit/${entry?.id}`}
                  />
                  {isAdmin && loggedInUser?.id !== entry?.id && (
                    <SubMenuItem
                      text={t('common:logIn')}
                      leftIconClassName="ki-technology-4 !text-[16px]"
                      onClick={() => {
                        loginByUserId(entry?.id);
                      }}
                    />
                  )}
                  {!hasNoRole && !isModerator && (
                    <SubMenuItem
                      text={t('common:deactivate')}
                      disabled={!entry?.active || pendingDeactivate}
                      leftIconClassName="ki-cross !text-[19px]"
                      textClassName="line-clamp-1"
                      onClick={() => onDeactivate([entry])}
                    />
                  )}
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
